import { createFileRoute, useBlocker } from '@tanstack/react-router'
import { addWeeks, endOfDay, startOfDay } from 'date-fns'
import { useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { LateOrderWorkOrder } from '@/api/hooks/useLateOrders'
import { useLateOrders } from '@/api/hooks/useLateOrders'
import { useUsersByWorkshop } from '@/api/hooks/useUsersByWorkshop'
import { useWorkflows } from '@/api/hooks/useWorkflow'
import { useWorkshopCapacity } from '@/api/hooks/useWorkshopCapacity'
import PageLayout from '@/components/layout/page-layout'
import type {
  CapacityData,
  CapacityScheduleProps,
  ReplanDateChangedWorkOrder,
} from '@/components/organisms/capacity-schedule'
import {
  buildJobColors,
  CapacitySchedule,
  getOverCapacityDays,
  ReplanNotifyCustomersDialog,
  transformCapacityData,
  transformDayTypes,
  useAccumulatedCapacity,
  useBulkReplanPreview,
} from '@/components/organisms/capacity-schedule'
import LateOrdersPanel, {
  type LateOrdersPanelView,
  LateOrdersToggleButtons,
} from '@/components/organisms/capacity-schedule/late-orders-panel'
import { ConfirmDialog } from '@/components/organisms/confirm-dialog'
import { Badge } from '@/components/ui/badge'
import { useMyWorkshops } from '@/hooks/useMyWorkshops'
import { cn } from '@/lib/utils'
import { getLateWorkOrderSteps, partitionJobsForReplan } from '@/utils/late-work-order-utils'
import { PermissionsUtils } from '@/utils/permissions.utils'

export const Route = createFileRoute('/_authenticated/capacity')({
  component: CapacityPage,
})

function CapacityPage() {
  const { t } = useTranslation()
  const [range, setRange] = useState<{ from: Date; to: Date }>({
    from: startOfDay(new Date()),
    to: endOfDay(addWeeks(startOfDay(new Date()), 4)),
  })

  const { selectedWorkshopId } = useMyWorkshops()
  // No `= []` default: a fresh array each render would look like a new response to the accumulator.
  const { data: workshopCapacity, isLoading: isCapacityLoading } = useWorkshopCapacity({
    workshopId: selectedWorkshopId,
    from: range.from.toDateString(),
    to: range.to.toDateString(),
  })

  const { data: users = [], isPending: usersLoading } = useUsersByWorkshop(selectedWorkshopId)

  const { data: workflows } = useWorkflows(selectedWorkshopId)
  // Late/over-capacity flagging only applies to work orders still being actively scheduled — the
  // late-orders endpoint returns just the planning + workshop stages, with their jobs.
  const { data: workOrders, isPending: isWorkOrdersPending } = useLateOrders(selectedWorkshopId, {
    enabled: !!selectedWorkshopId,
  })
  const lateSteps = useMemo(
    () => (workflows ? getLateWorkOrderSteps({ workOrders, workflows }) : []),
    [workOrders, workflows],
  )
  const lateWorkOrderIds = useMemo(
    () => Array.from(new Set(lateSteps.map(step => step.workOrderId))),
    [lateSteps],
  )
  // Jobs the work order has already moved past in its workflow (see partitionJobsForReplan) are
  // left out here — the late-orders/over-capacity panel is about what still needs attention, so an
  // already-done job type shouldn't clutter either list's per-car breakdown.
  const jobsByWorkOrderId = useMemo(() => {
    const map: Record<string, LateOrderWorkOrder['jobs']> = {}
    if (!workflows) return map
    for (const workOrder of workOrders) {
      const { eligibleJobIds } = partitionJobsForReplan({ workOrder, workflows })
      map[workOrder.id] = (workOrder.jobs ?? []).filter(job => eligibleJobIds.has(job.id))
    }
    return map
  }, [workOrders, workflows])

  const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<Set<string>>(new Set())
  const [isReplanMode, setIsReplanMode] = useState(false)
  // Only meaningful while isReplanMode is true; reset alongside it so re-entering preview mode
  // always starts by showing the preview, not whatever the user last left it on.
  const [showPreviewData, setShowPreviewData] = useState(true)
  const [isLateOrdersPanelOpen, setIsLateOrdersPanelOpen] = useState(false)
  // Kept separate from the open flag so the panel keeps its content while animating closed.
  const [panelView, setPanelView] = useState<LateOrdersPanelView>('late')
  // Set from a late-orders/over-capacity/replan-review row's onMouseEnter, so hovering a license
  // plate there highlights just that work order on the schedule — as the sole
  // `highlightedWorkOrderIds` entry outside replan mode, or as `colorOverrideWorkOrderId` during
  // it (see scheduleProps below for why those differ).
  const [hoveredWorkOrderId, setHoveredWorkOrderId] = useState<string | null>(null)
  const lateOrdersPanelId = useId()
  const isLateOrdersLoading = isWorkOrdersPending || !selectedWorkshopId
  const [dateChangedWorkOrders, setDateChangedWorkOrders] = useState<ReplanDateChangedWorkOrder[]>(
    [],
  )

  const exitReplanMode = () => {
    setIsReplanMode(false)
    setShowPreviewData(true)
    setHoveredWorkOrderId(null)
  }

  const replanPreview = useBulkReplanPreview({
    active: isReplanMode,
    selectedWorkOrderIds,
    activeWorkOrders: workOrders,
    workflows,
    workshopId: selectedWorkshopId,
    users,
    onSaved: dateChanged => {
      setSelectedWorkOrderIds(new Set())
      exitReplanMode()
      if (PermissionsUtils.hasPermission('communication:create')) {
        setDateChangedWorkOrders(dateChanged)
      }
    },
  })

  const { status: blockerStatus, proceed: proceedLeaving, reset: cancelLeaving } = useBlocker({
    withResolver: true,
    shouldBlockFn: ({ current, next }) => isReplanMode && current.pathname !== next.pathname,
    enableBeforeUnload: () => isReplanMode,
  })

  const handleToggleWorkOrder = (workOrderId: string) => {
    setSelectedWorkOrderIds(prev => {
      const next = new Set(prev)
      if (next.has(workOrderId)) next.delete(workOrderId)
      else next.add(workOrderId)
      return next
    })
  }

  const handleToggleAll = () => {
    setSelectedWorkOrderIds(prev =>
      prev.size === lateWorkOrderIds.length ? new Set() : new Set(lateWorkOrderIds),
    )
  }

  const handleClearSelection = () => {
    setSelectedWorkOrderIds(new Set())
    setHoveredWorkOrderId(null)
  }

  const handleCloseLateOrdersPanel = () => {
    handleClearSelection()
    setIsLateOrdersPanelOpen(false)
  }

  const handleToggleLateOrdersPanel = (view: LateOrdersPanelView) => {
    if (isLateOrdersPanelOpen && view === panelView) {
      handleCloseLateOrdersPanel()
      return
    }
    if (isLateOrdersPanelOpen) handleClearSelection()
    setPanelView(view)
    setIsLateOrdersPanelOpen(true)
  }

  // The query holds no cache, so on its own a week step would blank the whole grid. Accumulating
  // responses keeps the weeks already fetched on screen while only the new one loads.
  const { response: capacity, loadedDayIds } = useAccumulatedCapacity(workshopCapacity, range)
  const overCapacityDays = useMemo(() => getOverCapacityDays(capacity, new Date()), [capacity])

  const jobs = useMemo(
    () =>
      capacity.map(({ workGroup }) => ({
        id: workGroup,
        label: t(`workTypeGroup_${workGroup}`, { defaultValue: workGroup }),
      })),
    [capacity, t],
  )

  // Memoized because the accumulated response grows as the user navigates.
  const data: CapacityData = useMemo(() => transformCapacityData(capacity), [capacity])
  const dayTypes = useMemo(() => transformDayTypes(capacity), [capacity])
  const jobColors = useMemo(() => buildJobColors(capacity), [capacity])

  const normalScheduleProps = {
    jobs,
    data,
    dayTypes,
    jobColors,
    capacityData: capacity,
    // The query is disabled until a workshop is resolved, so treat that gap as loading too.
    isLoading: isCapacityLoading || !selectedWorkshopId,
    loadedDayIds,
    // Nothing is highlighted at rest here, so a hovered late-orders/over-capacity row can safely
    // become the sole entry — unlike in replan mode below, there's no existing highlighted set
    // whose positions this would disturb.
    highlightedWorkOrderIds: hoveredWorkOrderId ? new Set([hoveredWorkOrderId]) : undefined,
  }

  // Current (real) data with the affected work orders highlighted, rather than the computed
  // preview — same idea as normalScheduleProps, just carrying the preview's highlight/color info.
  const currentDataWithHighlightsProps = {
    ...normalScheduleProps,
    highlightedWorkOrderIds: replanPreview.schedule.highlightedWorkOrderIds,
    jobColors: replanPreview.schedule.jobColors,
  }

  let scheduleProps: CapacityScheduleProps = normalScheduleProps
  if (isReplanMode) {
    scheduleProps = {
      ...(showPreviewData ? replanPreview.schedule : currentDataWithHighlightsProps),
      // highlightedWorkOrderIds stays the full affected set in both branches above — hovering a
      // plate in the review panel narrows only the *color* to one car, so the rest never shift
      // position (see capacity-column.tsx's showJobColor/showHighlightFallback).
      colorOverrideWorkOrderId: hoveredWorkOrderId,
    }
  }

  return (
    <PageLayout title={t('global.capacity')}>
      <div
        className={cn(
          'relative rounded-md',
          isReplanMode && 'ring-2 ring-warning ring-offset-2 ring-offset-layer-1',
        )}
      >
        {isReplanMode && (
          <Badge className="absolute -top-3 left-4 z-10 border-transparent bg-warning text-warning-foreground">
            {t('capacitySchedule.replanPreviewBadge')}
          </Badge>
        )}
        <CapacitySchedule
          onRangeChange={(from, to) => setRange({ from, to })}
          showWorkOrderDateNav={false}
          workshopId={selectedWorkshopId}
          users={users}
          usersLoading={usersLoading}
          {...scheduleProps}
        />
      </div>
      {createPortal(
        <LateOrdersToggleButtons
          lateSteps={lateSteps}
          overCapacityDays={overCapacityDays}
          isLoading={isLateOrdersLoading}
          isOpen={isLateOrdersPanelOpen}
          view={panelView}
          onToggle={handleToggleLateOrdersPanel}
          contentId={lateOrdersPanelId}
        />,
        document.querySelector('#app-header') ?? document.body,
      )}
      <LateOrdersPanel
        lateSteps={lateSteps}
        overCapacityDays={overCapacityDays}
        jobsByWorkOrderId={jobsByWorkOrderId}
        isLoading={isLateOrdersLoading}
        isOpen={isLateOrdersPanelOpen}
        view={panelView}
        contentId={lateOrdersPanelId}
        onToggle={handleCloseLateOrdersPanel}
        selectedWorkOrderIds={selectedWorkOrderIds}
        onToggleWorkOrder={handleToggleWorkOrder}
        onToggleAll={handleToggleAll}
        onClearSelection={handleClearSelection}
        onReplanSelected={() => setIsReplanMode(true)}
        onHoverWorkOrder={setHoveredWorkOrderId}
        replanReview={
          isReplanMode
            ? {
                ...replanPreview.review,
                onCancel: exitReplanMode,
                showPreviewData,
                onShowPreviewDataChange: setShowPreviewData,
                onHoverWorkOrder: setHoveredWorkOrderId,
              }
            : undefined
        }
      />
      <ConfirmDialog
        open={blockerStatus === 'blocked'}
        onOpenChange={open => {
          if (!open) cancelLeaving?.()
        }}
        onConfirm={() => proceedLeaving?.()}
        title={t('bulkReplanPreview.leaveConfirmTitle')}
        description={t('bulkReplanPreview.leaveConfirmDescription')}
        cancelText={t('bulkReplanPreview.leaveConfirmCancel')}
        confirmText={t('bulkReplanPreview.leaveConfirmConfirm')}
      />
      {dateChangedWorkOrders.length > 0 && selectedWorkshopId && (
        <ReplanNotifyCustomersDialog
          workOrders={dateChangedWorkOrders}
          workshopId={selectedWorkshopId}
          onClose={() => setDateChangedWorkOrders([])}
        />
      )}
    </PageLayout>
  )
}
